(() => {
  const VALID_COLUMNS = [3, 4, 5];
  const DESKTOP_CLASS_PATTERN = /^grid--\d+-col-desktop$/;
  const PRODUCT_CARD_SELECTOR = '.product-card-wrapper';
  const STORAGE_PREFIX = 'collection-grid-columns';

  const getProductGrid = (root = document, sectionId = null) => {
    if (sectionId) {
      const scopedGrid = Array.from(document.querySelectorAll('#product-grid')).find(
        (grid) => grid.dataset.id === sectionId
      );
      if (scopedGrid) return scopedGrid;
    }

    if (root?.querySelector) {
      const grid = root.querySelector('#product-grid');
      if (grid) return grid;
    }

    return document.getElementById('product-grid');
  };

  const getToggleWrappers = (root = document) => {
    const wrappers = new Set();
    if (root?.querySelectorAll) {
      root.querySelectorAll('[data-columns-toggle-wrapper]').forEach((wrapper) => wrappers.add(wrapper));
    }
    document.querySelectorAll('[data-columns-toggle-wrapper]').forEach((wrapper) => wrappers.add(wrapper));
    return Array.from(wrappers);
  };

  const getWrapperSectionId = (wrapper) => {
    return wrapper.dataset.columnsToggleSection || wrapper.closest('#ProductGridContainer')?.querySelector('#product-grid')?.dataset.id;
  };

  const getStorageKey = (grid) => {
    const sectionId = grid?.dataset.id || 'default';
    return `${STORAGE_PREFIX}:${window.location.pathname}:${sectionId}`;
  };

  const getStoredColumns = (grid) => {
    try {
      return Number(window.sessionStorage.getItem(getStorageKey(grid)));
    } catch (error) {
      return 0;
    }
  };

  const storeColumns = (grid, columns) => {
    try {
      window.sessionStorage.setItem(getStorageKey(grid), String(columns));
    } catch (error) {
      // Storage can be unavailable in private browsing; the live data attribute still preserves this render.
    }
  };

  const getDefaultColumns = (grid) => Number(grid?.dataset.desktopColumns) || 4;

  const clampColumns = (grid, value) => {
    const parsedValue = Number(value);
    return VALID_COLUMNS.includes(parsedValue) ? parsedValue : getDefaultColumns(grid);
  };

  const removeDesktopColumns = (grid) => {
    grid.classList.forEach((className) => {
      if (DESKTOP_CLASS_PATTERN.test(className)) {
        grid.classList.remove(className);
      }
    });
  };

  const setActiveState = (wrapper, columns) => {
    wrapper.querySelectorAll('[data-columns-toggle]').forEach((button) => {
      const isActive = Number(button.dataset.columnsToggle) === columns;
      button.setAttribute('aria-pressed', isActive);
      button.classList.toggle('collection__column-toggle-button--active', isActive);
    });
  };

  const applyColumns = (grid, columns, persist = false) => {
    if (!grid?.classList) return;

    const parsedColumns = clampColumns(grid, columns);
    removeDesktopColumns(grid);
    grid.classList.add(`grid--${parsedColumns}-col-desktop`);
    grid.dataset.activeColumns = parsedColumns;

    if (persist) {
      storeColumns(grid, parsedColumns);
    }

    getToggleWrappers().forEach((wrapper) => {
      const sectionId = getWrapperSectionId(wrapper);
      const wrapperGrid = getProductGrid(wrapper.closest('#ProductGridContainer') || document, sectionId);
      if (wrapperGrid === grid) {
        setActiveState(wrapper, parsedColumns);
      }
    });
  };

  const syncToggleVisibility = (wrapper, grid) => {
    const productCards = grid?.querySelectorAll(PRODUCT_CARD_SELECTOR) || [];
    const hasProducts = productCards.length > 0;
    wrapper.hidden = !hasProducts;
    wrapper.setAttribute('aria-hidden', hasProducts ? 'false' : 'true');
  };

  const initializeWrapper = (wrapper) => {
    const sectionId = getWrapperSectionId(wrapper);
    const grid = getProductGrid(wrapper.closest('#ProductGridContainer') || document, sectionId);
    const toggleButtons = wrapper.querySelectorAll('[data-columns-toggle]');

    if (!grid || !toggleButtons.length) {
      wrapper.hidden = true;
      wrapper.setAttribute('aria-hidden', 'true');
      return;
    }

    const activeColumns = clampColumns(grid, getStoredColumns(grid) || Number(grid.dataset.activeColumns) || getDefaultColumns(grid));
    applyColumns(grid, activeColumns);
    syncToggleVisibility(wrapper, grid);
  };

  const initialize = (root = document) => {
    getToggleWrappers(root).forEach(initializeWrapper);
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-columns-toggle]');
    if (!button) return;

    const wrapper = button.closest('[data-columns-toggle-wrapper]');
    if (!wrapper) return;

    const sectionId = getWrapperSectionId(wrapper);
    const grid = getProductGrid(wrapper.closest('#ProductGridContainer') || document, sectionId);
    if (!grid) return;

    event.preventDefault();
    applyColumns(grid, Number(button.dataset.columnsToggle), true);
    syncToggleVisibility(wrapper, grid);
  });

  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  window.initializeCollectionColumns = initialize;
})();
